import {useCallback, useEffect, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import queryString from 'query-string';
import {ListTable} from '../common/ListTable';
import {ListPagination} from '../common/ListPagination';
import Container from '../common/layout/Container';
import Error from '../common/Error';
import {getSortedParams} from '../../helpers';
import Spinner from '../common/Spinner';
import ConfirmationPopup from '../common/popup/ConfirmationPopup';
import AddOperatory from './AddOperatory';
import {
  addOperatory, updateOperatoryById, deleteOperatoryById, getOperatories
} from '../../api/operatories';
import RequestError from '../../api/RequestError';
import {sanitize} from '../../helpers';
import {handleApiValidationErrors} from '../../helpers/error';
import {
  OperatoryListProps, OperatoryResponseProps, AddOperatoryProps
} from '../../constants/types';
import permissionsInfo from '../../constants/permissionsInfo';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {setTitle} from '../../store/header/header.slice';
import url from 'url';
import NoItems from '../common/no_items/NoItems';
import {noItemsMessages} from '../../constants/constants';
import Row from './Row';
import {DEFAULT_ERROR} from '../../api/constants';
import {useTranslation} from 'react-i18next';
import {useSnackbarHook} from '../../hooks/useSnackbar';

const entity = {
  fetchUrl: () => '/operatories',
  header: 'Operatories Configuration',
  title: 'List of Operatories',
  permissions: permissionsInfo.operatories.view,
  loadingText: 'Getting operatories list',
  fields: [{
    name: 'desc',
    label: 'Description',
    permission: permissionsInfo.operatories.view
  },
  {
    name: 'number',
    label: 'Operatory #',
    permission: permissionsInfo.operatories.view
  },
  {
    name: 'delete',
    label: '',
    className: 'cell header-cell narrow-cell',
    permission: permissionsInfo.operatories.delete
  }
  ]
};

export type OperatoryToDeleteProps = {
  id: number,
  message: string
}

const initialQuery = {
  page: '1',
  per_page: '10'
};

export const Operatories = () => {
  const {t} = useTranslation('operatories');
  const history = useHistory();
  const dispatch = useAppDispatch();
  const {pathname, search} = useLocation();
  const {activeTenant} = useAppSelector(({permissions}) => permissions);
  const [operatoryToDelete, setOperatoryToDelete] =
    useState<Nullable<OperatoryToDeleteProps>>(null);
  const [operatoriesData, setOperatoriesData] =
    useState<Nullable<OperatoryResponseProps>>(null);
  const [isLoading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string>('');
  const [apiValidationError, setApiValidationError] =
   useState<Nullable<Dictionary<string []>>>(null);
  const [toggleAddForm, setToggleAddForm] = useState(false);
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  const query = {
    ...initialQuery,
    ...getSortedParams(queryString.parse(search))
  };

  const getAllOperatories = async () => {
    setLoading(true);
    try {
      const response = await getOperatories(query);
      setOperatoriesData(response);
    } catch (error) {
      setError(error?.message || DEFAULT_ERROR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (query.page !== '1' && operatoriesData) {
      history.push(url.format({pathname, query: {...query, page: 1}}));
    }
  }, [activeTenant]);

  useEffect(() => {
    getAllOperatories();

    return () => {
      setError('');
    };
  }, [query?.page, query?.per_page]);

  useEffect(() => {
    history.replace(url.format({pathname, query}));
    dispatch(setTitle(''));
  }, []);

  const {
    content = [], pageNumber = 1, totalElements = 0, totalPages = 1
  } = operatoriesData || {};
  const contentLength = content?.length;

  const noItems = !totalElements && !isLoading;

  const closeModal = useCallback(() => {
    setOperatoryToDelete(null);
  }, []);

  const clearErrors = () => {
    setApiValidationError(null);
    setError('');
  };

  const handleToggleAddForm = useCallback(() => {
    setToggleAddForm((prevState) => !prevState);
  }, []);

  const handleAddOperatory = async (
      values: AddOperatoryProps,
      resetForm: () => void
  ) => {
    try {
      const requestBody = sanitize(values);
      await addOperatory(requestBody);
      addSuccessToast(t('toasts.addOperatoryMessage'));
      const shouldGoToLastPagePlusOne =
        totalElements % Number(query.per_page) === 0;
      const shouldGoToLastPage = totalPages > Number(query.page);

      if (shouldGoToLastPagePlusOne && content?.length) {
        history.push(url.format({
          pathname,
          query: {...query, page: `${totalPages + 1}`}
        }));
      } else if (shouldGoToLastPage) {
        history.push(url.format({
          pathname,
          query: {...query, page: `${totalPages}`}
        }));
      } else {
        const response = await getOperatories(query);
        setOperatoriesData(response);
        resetForm();
      }
    } catch (error) {
      if (error instanceof RequestError && error?.validationErrors) {
        const errors = handleApiValidationErrors(error, 'operatory');
        setApiValidationError(errors);
      } else {
        addErrorToast(error?.message);
      }
    }
  };

  const handleEditOperatory = async (id, data) => {
    const {content} = operatoriesData || {};

    try {
      const currentOperatory = content
          ?.find((operatory: OperatoryListProps) => operatory?.id === id);

      const {desc, number} = currentOperatory || {};

      const requestBody = sanitize({
        desc,
        number,
        ...data
      });

      await updateOperatoryById(id, requestBody);
      addSuccessToast(t('toasts.updateOperatoryMessage'));
      const response = await getOperatories(query);
      setOperatoriesData(response);
    } catch (error) {
      addErrorToast(error?.message);
    }
  };

  const handleDeleteOperatory = async () => {
    if (!operatoryToDelete) return;

    setIsDeleting(true);
    const {id} = operatoryToDelete;

    try {
      await deleteOperatoryById(id);
      addSuccessToast(t('toasts.removeOperatoryMessage'));

      const shouldGoToPreviousPage = content.length === 1 && query.page !== '1';
      if (shouldGoToPreviousPage) {
        history.push(url.format({
          pathname,
          query: {...query, page: `${Number(query?.page) - 1}`}
        }));
      } else {
        const response = await getOperatories(query);
        setOperatoriesData(response);
      }
    } catch (error) {
      addErrorToast(error?.message);
    } finally {
      setIsDeleting(false);
      closeModal();
    }
  };

  const InlineAddComponent = (
    <AddOperatory
      toggleAddForm={toggleAddForm}
      handleAddOperatory={handleAddOperatory}
      apiValidationError={apiValidationError}
      clearErrors={clearErrors}
    />
  );

  const isDeleteIconVisible = totalElements > 1;

  return (
    <>
      <Container header={entity?.header}>
        <>
          <div className="list-header">
            <h2 className="subheader">{entity?.title}</h2>
          </div>

          {isLoading && !operatoriesData ?
            <Spinner loadingText={entity?.loadingText} /> :
            <Spinner loadingText={entity?.loadingText} isLoading={isLoading}>
              <>
                <ListTable
                  rowRenderer={(content) => content.map((operatoryData) => (
                    <Row
                      key={`${operatoryData.id}`}
                      operatory={operatoryData}
                      editHandler={handleEditOperatory}
                      handleToggleAddForm={handleToggleAddForm}
                      setOperatoryToDelete={setOperatoryToDelete}
                      isDeleteIconVisible={isDeleteIconVisible}
                    />
                  ))}
                  entity={entity}
                  content={content}
                  query={query}
                  inlineAdd={InlineAddComponent}
                />

                {noItems ?
                  (<NoItems message={noItemsMessages.ITEMS}/>) :
                  (<ListPagination
                    contentLength={contentLength}
                    pageNumber={pageNumber}
                    totalElements={totalElements}
                    totalPages={totalPages}
                    query={query}
                  />)}
              </>
            </Spinner>}

          {error && <Error message={error} />}
        </>
      </Container>

      <ConfirmationPopup
        labels={{body: operatoryToDelete?.message || ''}}
        isOpen={!!operatoryToDelete}
        setIsOpen={closeModal}
        confirmableAction={handleDeleteOperatory}
        isActionProcessing={isDeleting}
      />
    </>
  );
};

export default Operatories;
